import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { EditableColumnDef, EditableTable } from "../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../shared/components/buttons/custom-button";
import { StandardDetails } from "../../../sc-models/scope-certificate-details-dto";
import { OptionType } from "../../../../../shared/components/form-builder/form-config.model";
import useNotification from "../../../../../shared/hooks/useNotification";
import { useParams } from "react-router-dom";
import { useScStore } from "../../../../../store/scStandard.store";
import { useTransactionCertificateStore } from "../../../../../store/transaction-certificates.store";


interface StandardDetailsFormProps {
  ccsFlag:string;
  standardData: StandardDetails[];
  setStandardData: React.Dispatch<React.SetStateAction<StandardDetails[]>>;
}

const validationSchema = Yup.object().shape({
  scStandard: Yup.string().required("SC Standard is required"),
  scStandardDescription: Yup.string(),
  scStandardVersion: Yup.string().nullable(),
  scNumberOfFarms: Yup.string(),
  numberOfAuditDays: Yup.string(),
  certifiedOrganizationApprovedClaims: Yup.string(),
  certifiedOrganizationApprovedClaimsDescription: Yup.string()
});

const StandardDetailsForm: React.FC<StandardDetailsFormProps> = ({
  ccsFlag,
  standardData,
  setStandardData,
}) => {
  const { formtype } = useParams();
  const hasSubmitSC: boolean = formtype === 'submited-sc';
  const [data, setData] = useState<StandardDetails[]>(standardData);
  const [tableData, setTableData] = useState<StandardDetails[]>(standardData);
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const { notify } = useNotification();
  const { setScStandards } = useScStore();
  const { setRestrictPopUpSave, isRestrictPopUpSave } = useTransactionCertificateStore();

  const scStandardValues: OptionType[] = [
    { label: "Organic Content Standard (OCS)", value: "Organic Content Standard (OCS)", code: "OCS" }, // Represents "Valid"
    { label: "Global Recycled Standard (GRS)", value: "Global Recycled Standard (GRS)", code: "GRS" }, // Represents "Expired" 
    { label: "Recycled Claim Standard (RCS)", value: "Recycled Claim Standard (RCS)", code: "RCS" }, // Represents "Suspended"
    { label: "Responsible Down Standard (RDS)", value: "Responsible Down Standard (RDS)", code: "RDS" }, // Represents "Withdrawn"
    { label: "Responsible Wool Standard (RWS)", value: "Responsible Wool Standard (RWS)", code: "RWS" }, // Represents "Withdrawn"
    { label: "Responsible Mohair Standard (RMS)", value: "Responsible Mohair Standard (RMS)", code: "RMS" }, // Represents "Withdrawn"
    { label: "Responsible Alpaca Standard (RAS)", value: "Responsible Alpaca Standard (RAS)", code: "RAS" }, // Represents "Withdrawn"
    { label: "Content Claim Standard (CCS)", value: "Content Claim Standard (CCS)", code: "CCS" }, // Represents "Withdrawn"
    // ...ccsFlag==="Yes"?[]:[{ label: "Sustainable Fiber Alliance (SFA)", value: "SFA (SFA)", code: "SFA" }], // Represents "Withdrawn"
    // { label: "Global Organic Textile Standard (GOTS)", value: "Global Organic Textile Standard (GOTS)", code: "GOTS" }, // Represents "Withdrawn"
  ];

  const certifiedOrganizationApprovedClaimsValues: OptionType[] = [
    { label: "None", value: "", code: "" }, // Represents "Valid"
    { label: "Product Claims (PC)", value: "Product Claims (PC)", code: "PC" }, // Represents "Valid"
    { label: "Non-Product Claims (NPC)", value: "Non-Product Claims (NPC)", code: "NPC" }, // Represents "Expired" 
    { label: "Product and Non-Product Claims (PNPC)", value: "Product and Non-Product Claims (PNPC)", code: "PNPC" }, // Represents "Suspended"
    { label: "No Claims (NC)", value: "No Claims (NC)", code: "NC" }, // Represents "Withdrawn"
  ];

  const formik = useFormik<StandardDetails>({
    initialValues: {
      scStandard: "",
      scStandardDescription: "",
      scStandardVersion: "",
      scNumberOfFarms: "",
      numberOfAuditDays: "",
      certifiedOrganizationApprovedClaims: "",
      certifiedOrganizationApprovedClaimsDescription: ""
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    
    console.log("columnId :: ", columnId);
    console.log("value :: ", value);
    

    if(columnId === 'certifiedOrganizationApprovedClaimsDescription') {
      let optionCOde = certifiedOrganizationApprovedClaimsValues.find(val => val.value === value)?.code || ""
      formik.setFieldValue('certifiedOrganizationApprovedClaimsDescription', value);
      formik.setFieldValue('certifiedOrganizationApprovedClaims', optionCOde);
        setData((prevData) =>
          prevData.map((row, index) =>
            index === rowIndex ? { ...row, ['certifiedOrganizationApprovedClaimsDescription']: value, 'certifiedOrganizationApprovedClaims': optionCOde } : row
          )
        );
    } else if(columnId === 'scStandardDescription') {
      let optionCOde = scStandardValues.find(val => val.value === value)?.code || ""
      formik.setFieldValue('scStandardDescription', value);
      formik.setFieldValue('scStandard', optionCOde);
        setData((prevData) =>
          prevData.map((row, index) =>
            index === rowIndex ? { ...row, ['scStandardDescription']: value, 'scStandard': optionCOde } : row
          )
        );
    } else {
      formik.setFieldValue(columnId, value);
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row
        )
      );
    }
    
  };

  const handleAddNewRow = () => {
    const newRow: StandardDetails = {
      scStandard: "",
      scStandardDescription: "",
      scStandardVersion: "",
      scNumberOfFarms: "",
      numberOfAuditDays: "",
      certifiedOrganizationApprovedClaims: "",
      certifiedOrganizationApprovedClaimsDescription: ""
    };
    setRestrictPopUpSave(true);
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    // const currentRowValues = formik.values;

    // // Determine if the row being edited is new by comparing `editingRowIndex` with the data length
    // const isNewRow = editingRowIndex === data.length - 1;

    // try {
    //   // Validate the row using the Formik validation schema
    //   await validationSchema.validate(currentRowValues, { abortEarly: false });

    //   if (isNewRow) {
    //     // If validation passes and it's a new row, keep it in the data
    //     setData((prevData) => prevData.map((row, index) => (index === editingRowIndex ? currentRowValues : row)));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // } catch (validationErrors) {
    //   if (isNewRow) {
    //     // If validation fails and it's a new row, remove it from the data
    //     setData((prevData) => prevData.slice(0, -1));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // }
    setRestrictPopUpSave(false);
    setData(tableData);
    // Reset editing state and form
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const handleSaveRow = () => {
    // Todo on click save the data is not saving in the row due to the below code, need to investigate
    // if (editingRowIndex === null || !formik.isValid) return;
    if (editingRowIndex === null || !formik.isValid) {
      notify('Enter the mandatory fields', 'error');
      return;
    }

    const updatedRow = formik.values;
    const newData = data.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );
    setData(newData);
    setTableData(newData);

    const allStandards = data.map(row => row.scStandard);
    setScStandards(allStandards);
    setRestrictPopUpSave(false);
    setStandardData(data); // Update parent state
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  useEffect(() => {
    const currentStore = useScStore.getState();
    console.log("Updated store value:", currentStore.scStandards); // Dusra console log
    return (()=>{
      setRestrictPopUpSave(false);
    })
  }, []);

  const columns: EditableColumnDef<StandardDetails>[] = [
    {
      accessorKey: "scStandardDescription",
      header: "SC Standard",
      Header: <span> <i style={{ color: 'red' }}>* </i>SC Standard</span>,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          name="scStandardDescription"
          value={value}
          placeholder="Select SC Standard"
          options={scStandardValues}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
      ),
    },
    {
      accessorKey: "scStandardVersion",
      header: "SC Standard Version",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="scStandardVersion"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter SC Standard Version"
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "scNumberOfFarms",
      header: "SC No. of Farms",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="scNumberOfFarms"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (!/^[0-9]$/.test(e.key) && e.key !== "Backspace" && e.key !== "Delete") {
              e.preventDefault();
            }
          }}
          placeholder="Enter Number of Farms"
          type="number"
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "numberOfAuditDays",
      header: "No. of Audit Days",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="numberOfAuditDays"
          value={value}
          onKeyDown={(e) => {
            if (!/^[0-9]$/.test(e.key) && e.key !== "Backspace" && e.key !== "Delete") {
              e.preventDefault();
            }
          }}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Number of Audit Days"
          type="number"
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "certifiedOrganizationApprovedClaimsDescription",
      header: "Certified Organization Approved Claims",
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          name="certifiedOrganizationApprovedClaimsDescription"
          value={value}
          placeholder="Enter Approved Claims"
          options={certifiedOrganizationApprovedClaimsValues}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setRestrictPopUpSave(true);
    setEditingRowIndex(rowIndex);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setStandardData(updatedData); // Update parent with updated data
    const remainingStandards = updatedData.map(row => row.scStandard);
    console.log("Standards after deletion:", remainingStandards); // For debugging
    setScStandards(remainingStandards);
  };

  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton onClick={handleAddNewRow} sx={{ mb: 2 }} disabled={!formik.isValid || hasSubmitSC || isRestrictPopUpSave}>
            Add Standard Details
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
          enableEditing={!hasSubmitSC}
        />
      </Box>
    </div>
  );
};

export default StandardDetailsForm;
